import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building, 
  Brain, 
  Wifi, 
  RefreshCw, 
  AlertCircle,
  Activity,
  TrendingUp,
  Users,
  CheckCircle,
  Calendar,
  Phone,
  UserPlus,
  DollarSign,
  BarChart3,
  Target,
  Clock,
  Globe,
  ArrowRightLeft
} from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import api from '../../../../services/api';

/**
 * No array retornado por reelunet-selected, o bloco de IA de Vendas é o item que contém
 * total_registros e total_conversas (não o primeiro item AUTOMACAO_OS_REELUNET).
 */
function extrairPayloadIaVendasReelunet(payload) {
  if (!Array.isArray(payload)) return payload;
  const bloco = payload.find(
    (item) =>
      item &&
      typeof item === 'object' &&
      !item.AUTOMACAO_OS_REELUNET &&
      Object.prototype.hasOwnProperty.call(item, 'total_registros') &&
      Object.prototype.hasOwnProperty.call(item, 'total_conversas')
  );
  return bloco ?? payload[0];
}

/**
 * No array do webhook, `gasto_ia_mensal` pode vir em um objeto à parte; mescla no bloco de IA.
 */
function mesclarGastoIaMensalReelunet(payload, blocoIa) {
  if (!blocoIa || typeof blocoIa !== 'object' || Array.isArray(blocoIa)) return blocoIa;
  if (!Array.isArray(payload)) {
    if (payload && typeof payload === 'object' && 'gasto_ia_mensal' in payload) {
      return { ...blocoIa, gasto_ia_mensal: payload.gasto_ia_mensal };
    }
    return blocoIa;
  }
  const comGasto = payload.find(
    (item) =>
      item &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      Object.prototype.hasOwnProperty.call(item, 'gasto_ia_mensal')
  );
  if (comGasto && comGasto.gasto_ia_mensal !== undefined) {
    return { ...blocoIa, gasto_ia_mensal: comGasto.gasto_ia_mensal };
  }
  return blocoIa;
}

/** Total de clientes: Reelunet usa `total_clientes` do bloco IA; MOV Fibra mantém `TOTAL DE CLIENTES`. */
function lerTotalClientesIaVendas(dashboardData, variant) {
  if (!dashboardData) return 0;
  if (variant === 'reelunet') {
    const raw = dashboardData.total_clientes;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return dashboardData['TOTAL DE CLIENTES'] ?? 0;
}

function lerNaoCadastradosIaVendas(dashboardData, variant) {
  if (!dashboardData) return 0;
  if (variant === 'reelunet') {
    const raw = dashboardData.nao_cadastrados;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return dashboardData['NÃO CADASTRADOS'] ?? 0;
}

/** Reelunet: `quantidade_clientes_transferidos` do webhook. */
function lerQuantidadeClientesTransferidos(dashboardData, variant) {
  if (!dashboardData || variant !== 'reelunet') return 0;
  const raw = dashboardData.quantidade_clientes_transferidos;
  if (raw === undefined || raw === null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
}

/** Cadastros ativos (card “Total Cadastros Ativos” / “Cadastros ativados pela IA”): Reelunet usa `total_ativados_ia`; MOV Fibra usa TOTAL DE ATIVOS DE TODO O PERIODO. */
function lerCadastrosAtivosIaVendas(dashboardData, variant) {
  if (!dashboardData) return 0;
  if (variant === 'reelunet') {
    const raw = dashboardData.total_ativados_ia;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return dashboardData['TOTAL DE ATIVOS DE TODO O PERIODO'] ?? 0;
}

/** Cadastro mensal: Reelunet usa `cadastro_mensal` do webhook; MOV Fibra usa ATIVOS DO MÊS. */
function lerCadastroMensal(dashboardData, variant) {
  if (!dashboardData) return 0;
  if (variant === 'reelunet') {
    const raw = dashboardData.cadastro_mensal;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return dashboardData['ATIVOS DO MÊS'] ?? 0;
}

/** Contato mensal: Reelunet usa `contato_mensal` do webhook; MOV Fibra usa CONTATO MENSAL. */
function lerContatoMensal(dashboardData, variant) {
  if (!dashboardData) return 0;
  if (variant === 'reelunet') {
    const raw = dashboardData.contato_mensal;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return dashboardData['CONTATO MENSAL'] ?? 0;
}

/** Projeção de contatos restantes: Reelunet usa `projecao_contatos_restantes`; MOV Fibra usa PROJECAO_CONTATOS_RESTANTES. */
function lerProjecaoContatosRestantes(dashboardData, variant) {
  if (!dashboardData) return 0;
  if (variant === 'reelunet') {
    const raw = dashboardData.projecao_contatos_restantes;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return dashboardData['PROJECAO_CONTATOS_RESTANTES'] ?? 0;
}

/** Projeção cadastros: Reelunet usa `projecao_total_cadastros_mes`; MOV Fibra usa PROJECAO_CADASTROS_RESTANTES. */
function lerProjecaoCadastrosRestantes(dashboardData, variant) {
  if (!dashboardData) return 0;
  if (variant === 'reelunet') {
    const raw = dashboardData.projecao_total_cadastros_mes;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return dashboardData['PROJECAO_CADASTROS_RESTANTES'] ?? 0;
}

/** Custos IA mensal: Reelunet usa `gasto_ia_mensal` (ex.: string "R$ 0,00"); MOV Fibra usa CUSTOS DA AI VENDAS MENSAL. */
function lerGastoIaMensal(dashboardData, variant) {
  if (!dashboardData) return 'R$ 0,00';
  if (variant === 'reelunet') {
    const raw = dashboardData.gasto_ia_mensal;
    if (raw === undefined || raw === null || raw === '') return 'R$ 0,00';
    return String(raw);
  }
  return dashboardData['CUSTOS DA AI VENDAS MENSAL'] || 'R$ 0,00';
}

/** Percentuais vindos do webhook Reelunet; exibe "—" se null/vazio. */
function lerPercentualWebhook(val) {
  if (val === undefined || val === null || val === '') return '—';
  return String(val);
}

const MovFibraDashboard = ({ action, refreshKey = 0, variant = 'mov-fibra' }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/automacoes/dashboard', {
        params: { action }
      });
      
      const payload = response?.data?.data ?? response.data;
      let data;
      if (variant === 'reelunet') {
        data = extrairPayloadIaVendasReelunet(payload);
        if (data === undefined && payload && !Array.isArray(payload)) {
          data = payload;
        }
        data = mesclarGastoIaMensalReelunet(payload, data);
      } else {
        data = Array.isArray(payload) ? payload[0] : payload;
      }
      
      setDashboardData(data);
    } catch (err) {
      console.error('Erro ao carregar dashboard MOV Fibra:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [action, variant]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, refreshKey]);

  // Função para calcular conversão total (MOV Fibra) ou exibir conversao_total_pct (Reelunet)
  const calcularConversaoTotal = () => {
    if (!dashboardData) return variant === 'reelunet' ? '—' : '0%';
    if (variant === 'reelunet') {
      return lerPercentualWebhook(dashboardData.conversao_total_pct);
    }
    const totalClientes = Number(lerTotalClientesIaVendas(dashboardData, variant)) || 0;
    const totalAtivos = Number(lerCadastrosAtivosIaVendas(dashboardData, variant)) || 0;
    if (totalClientes === 0) return '0%';
    const percentual = ((totalAtivos / totalClientes) * 100).toFixed(2);
    return `${percentual}%`;
  };

  // Função para calcular conversão mensal (MOV Fibra) ou exibir conversao_mensal_pct (Reelunet)
  const calcularConversaoMensal = () => {
    if (!dashboardData) return variant === 'reelunet' ? '—' : '0%';
    if (variant === 'reelunet') {
      return lerPercentualWebhook(dashboardData.conversao_mensal_pct);
    }
    const contatoMensal = Number(lerContatoMensal(dashboardData, variant)) || 0;
    const ativosMes = Number(lerCadastroMensal(dashboardData, variant)) || 0;
    if (contatoMensal === 0) return '0%';
    const percentual = ((ativosMes / contatoMensal) * 100).toFixed(2);
    return `${percentual}%`;
  };

  const isPainelIaVendas =
    action === 'ai-vendas-selected' ||
    (variant === 'reelunet' && action === 'reelunet-selected');

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={false}
        className="min-h-[240px] w-full"
        cardClassName="max-w-md w-full"
        label={variant === 'reelunet' ? 'Carregando IA de Vendas (Reelunet)' : 'Carregando dashboard MOV Fibra'}
        subtitle="Aguarde enquanto atualizamos os dados."
      />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-2">Erro ao carregar dados</h3>
        <p className="text-[color:var(--text-muted)] mb-4">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="inline-flex items-center px-4 py-2 bg-[color:var(--accent)] text-white rounded-md hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho específico */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
              {variant === 'reelunet' ? (
                <Globe className="h-6 w-6 text-green-600" />
              ) : (
                <Building className="h-6 w-6 text-blue-600" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[color:var(--text-primary)]">
                {variant === 'reelunet' ? 'Reelunet' : 'MOV Fibra'}
              </h1>
              <p className="text-[color:var(--text-muted)]">
                {variant === 'reelunet'
                  ? 'Dashboard de automações da Reelunet — IA de Vendas'
                  : 'Dashboard de automações da MOV Fibra'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de métricas específicas da MOV Fibra */}
      {isPainelIaVendas ? (
        <>
          {/* Dados Gerais */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
              <Brain className="h-5 w-5 mr-2 text-purple-600" />
              Dados Gerais - IA de Vendas
            </h3>
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${
                variant === 'reelunet' ? 'xl:grid-cols-6' : 'xl:grid-cols-5'
              }`}
            >
              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Total de Clientes</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerTotalClientesIaVendas(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">
                      {variant === 'reelunet' ? 'Cadastros ativados pela IA' : 'Total Cadastros Ativos'}
                    </p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerCadastrosAtivosIaVendas(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Não Cadastrados</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerNaoCadastradosIaVendas(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              {variant === 'reelunet' && (
                <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                  <div className="flex items-center">
                    <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                      <ArrowRightLeft className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-[color:var(--text-muted)]">Clientes Transferidos</p>
                      <p className="text-xl font-bold text-[color:var(--text-primary)]">
                        {lerQuantidadeClientesTransferidos(dashboardData, variant)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Conversão Total</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {calcularConversaoTotal()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Média de Tempo de Cadastro</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {dashboardData?.['media_cadastro_geral_horas'] || '0h'}h
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dados Mensais */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-green-600" />
              Dados Mensais - IA de Vendas
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <Phone className="h-5 w-5 text-[color:var(--accent)]" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Contato Mensal</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerContatoMensal(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <UserPlus className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Cadastro Mensal</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerCadastroMensal(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Conversão Mensal</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {calcularConversaoMensal()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <DollarSign className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Custos IA Mensal</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerGastoIaMensal(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <Brain className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs sm:text-sm font-medium text-[color:var(--text-muted)]">Clientes cadastrados pela IA no mês</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {dashboardData?.['CADASTRADOS COM IA E ATIVOS DO MÊS'] || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <Users className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs sm:text-sm font-medium text-[color:var(--text-muted)]">Clientes repassados para vendedor e cadastrados</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {dashboardData?.['CADASTRADOS COM VENDEDOR E ATIVOS DO MÊS'] || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Projeções e Métricas Avançadas */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
              Projeções e Métricas Avançadas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <Target className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Projeção Contatos Restantes</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerProjecaoContatosRestantes(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                <div className="flex items-center">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                    <UserPlus className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Projeção Cadastros Restantes</p>
                    <p className="text-xl font-bold text-[color:var(--text-primary)]">
                      {lerProjecaoCadastrosRestantes(dashboardData, variant)}
                    </p>
                  </div>
                </div>
              </div>

              {variant !== 'reelunet' && (
                <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                  <div className="flex items-center">
                    <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                      <DollarSign className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-[color:var(--text-muted)]">Valor Recebido</p>
                      <p className="text-xl font-bold text-[color:var(--text-primary)]">
                        {dashboardData?.['VALOR RECEBIDO DE CLIENTES NA BASE QUE INSTALARAM'] || 'R$ 0,00'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {variant !== 'reelunet' && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-indigo-600" />
                Agendamentos
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-[color:var(--surface)] p-3 sm:p-4 rounded-lg border border-[color:var(--border)]">
                  <div className="flex items-center">
                    <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                      <Calendar className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-[color:var(--text-muted)]">Agendamentos Mensais</p>
                      <p className="text-xl font-bold text-[color:var(--text-primary)]">
                        {dashboardData?.total_agendamentos_mensal || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Dashboard para IA de Serviço de Redes */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Wifi className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Total de finalizações de Ordem de Serviço automáticas</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.total_os_finalizadas || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Valor total recebido</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.valor_total_recebido || 'R$ 0,00'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Finalizações Mensais</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.mes_atual?.total_os_mes || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <DollarSign className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Valor recebido no mês</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.mes_atual?.valor_recebido_mes || 'R$ 0,00'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovFibraDashboard;
